import React from 'react'
import {
  Button,
  Checkbox,
  Image,
  Modal,
  Rate,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import { reviewsApi } from 'api/reviews'
import { EReviewEntityType, EReviewStatus, IReview } from 'models/review'
import { MainTitle } from 'components/ui/MainTitle/MainTitle'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { superAdminRoutes } from 'shared/constants/routes'
import { IMAGES_URL } from 'shared/constants/urls'
import {
  getReviewEntityTypeLabel,
  getReviewStatusColor,
  getReviewStatusLabel
} from 'shared/constants/reviews'
import { showServerError } from 'shared/utils/showServerError'
import { showMessage } from 'shared/utils/showMessage'
import { getPendingReviewsCount } from 'store/reviews'
import styles from './ReviewsList.module.scss'

interface IReviewsListProps {
  // продавец видит только отзывы на свои товары, сразу во всех статусах, и не меняет их
  isSellerView?: boolean
  // карточка продавца у админа: только его отзывы, сразу во всех статусах
  sellerId?: string
}

const PAGE_SIZE = 20

const MODERATION_STATUSES = [EReviewStatus.PENDING, EReviewStatus.REJECTED]
const ALL_STATUSES = [
  EReviewStatus.PENDING,
  EReviewStatus.REJECTED,
  EReviewStatus.PUBLISHED
]

// Продавцу автора анонимного отзыва не раскрываем: на сайте он тоже скрыт
const getAuthorName = (review: IReview, hideAnonymous: boolean) => {
  if (review.anonymity && hideAnonymous) {
    return 'Анонимный отзыв'
  }

  const author = review.author

  if (!author) {
    return '—'
  }

  const name =
    author.full_name ||
    [author.surname, author.name, author.patronymic].filter(Boolean).join(' ')

  return name || author.email || (author.tel ? String(author.tel) : '—')
}

const getEntityName = (review: IReview) => {
  return review.entity?.name ?? '—'
}

const getMediaLabel = ({ images = [], videos = [] }: IReview) =>
  [
    videos.length ? `${videos.length} видео` : '',
    images.length ? `${images.length} фото` : ''
  ]
    .filter(Boolean)
    .join(', ')

// Ведём в админку, а не на сайт: у покупателя публичной страницы нет,
// а REACT_APP_SITE_URL — адрес самой панели. Товар и лошадь лежат
// по одинаковым путям в кабинете продавца и у админа, услуги — только у админа
const getEntityLink = (review: IReview, isSellerView: boolean) => {
  const id = review.entity?._id

  if (!id) {
    return null
  }

  switch (review.entity_type) {
  case EReviewEntityType.PRODUCT:
    return superAdminRoutes.GOODS_EDIT.replace(':id', id)
  case EReviewEntityType.HORSE:
    return superAdminRoutes.HORSE.replace(':id', id)
  case EReviewEntityType.SERVICE:
    return isSellerView
      ? null
      : superAdminRoutes.POLO_CLUB_SERVICE_EDIT.replace(':id', id)
  default:
    return null
  }
}

const renderDetailsValue = (name: string, link: string | null) =>
  link ? (
    <Typography.Link href={link} target='_blank' rel='noreferrer'>
      {name}
    </Typography.Link>
  ) : (
    <Typography.Text>{name}</Typography.Text>
  )

export const ReviewsList: React.FC<IReviewsListProps> = ({
  isSellerView = false,
  sellerId
}) => {
  const isMobile = useIsMobile()
  const dispatch = useAppDispatch()
  const [reviews, setReviews] = React.useState<IReview[]>([])
  const [total, setTotal] = React.useState(0)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [isLoading, setIsLoading] = React.useState(false)
  const [showPublished, setShowPublished] = React.useState(false)
  const [selectedReview, setSelectedReview] = React.useState<IReview | null>(
    null
  )
  const [isStatusLoading, setIsStatusLoading] = React.useState(false)

  const getReviews = React.useCallback(
    async (skip: number) => {
      try {
        setIsLoading(true)
        const request = {
          skip: String(skip),
          limit: String(PAGE_SIZE),
          statuses:
            isSellerView || sellerId || showPublished
              ? ALL_STATUSES
              : MODERATION_STATUSES,
          seller_id: sellerId
        }
        const response = isSellerView
          ? await reviewsApi.getMyList(request)
          : await reviewsApi.getList(request)
        setReviews(response.data.reviews)
        setTotal(response.data.count)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    },
    [isSellerView, sellerId, showPublished]
  )

  React.useEffect(() => {
    getReviews(0)
    setCurrentPage(1)
  }, [getReviews])

  const updateStatus = async (status: EReviewStatus) => {
    if (!selectedReview) {
      return
    }

    try {
      setIsStatusLoading(true)
      await reviewsApi.updateStatus(selectedReview._id, { status })
      showMessage(
        'success',
        status === EReviewStatus.PUBLISHED
          ? 'Отзыв опубликован'
          : 'Отзыв отклонён',
        ''
      )
      setSelectedReview(null)
      getReviews((currentPage - 1) * PAGE_SIZE)
      dispatch(getPendingReviewsCount())
    } catch (e) {
      showServerError(e)
    } finally {
      setIsStatusLoading(false)
    }
  }

  const columns: ColumnsType<IReview> = [
    {
      title: 'Автор отзыва',
      dataIndex: 'author',
      render: (_, review) =>
        !isSellerView && review.author?._id ? (
          <CardLink to={superAdminRoutes.USERS_EDIT.replace(':id', review.author._id)}>
            {getAuthorName(review, isSellerView)}
          </CardLink>
        ) : (
          <Typography.Text>{getAuthorName(review, isSellerView)}</Typography.Text>
        )
    },
    ...(isSellerView || sellerId
      ? []
      : [
        {
          title: 'Магазин',
          dataIndex: 'shop',
          render: (_: unknown, review: IReview) =>
            review.shop?._id && review.shop.shop_name ? (
              <CardLink to={superAdminRoutes.SELLER.replace(':id', review.shop._id)}>
                {review.shop.shop_name}
              </CardLink>
            ) : (
              <Typography.Text>{review.shop?.shop_name ?? '—'}</Typography.Text>
            )
        }
      ]),
    {
      title: 'Товар',
      dataIndex: 'entity',
      render: (_, review) => (
        <Typography.Text>{getEntityName(review)}</Typography.Text>
      )
    },
    {
      title: 'Фото и видео',
      dataIndex: 'images',
      render: (_, review) => (
        <Typography.Text>{getMediaLabel(review) || '—'}</Typography.Text>
      )
    },
    {
      title: 'Заказ',
      dataIndex: 'order',
      render: (_, review) => (
        <Typography.Text>{review.order?.number ?? '—'}</Typography.Text>
      )
    },
    {
      title: 'Дата отзыва',
      dataIndex: 'createdAt',
      render: (_, { createdAt, edited_at }) => (
        <Typography.Text>
          {moment(createdAt).locale('ru').format('LLL')}
          {edited_at && ' · изменён'}
        </Typography.Text>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (_, { status }) => (
        <Tag color={getReviewStatusColor(status)}>
          {getReviewStatusLabel(status)}
        </Tag>
      )
    }
  ]

  // На телефоне колонки собраны в две: отзыв с автором и датой, оценка со статусом
  const mobileColumns: ColumnsType<IReview> = [
    {
      title: 'Отзыв',
      dataIndex: 'entity',
      width: '60%',
      render: (_, review) => (
        <div className={styles.mobileCell}>
          <Typography.Text className={styles.mobileTitle}>
            {getEntityName(review)}
          </Typography.Text>
          <Typography.Text type='secondary' className={styles.mobileSubText}>
            {getAuthorName(review, isSellerView)}
          </Typography.Text>
          <Typography.Text type='secondary' className={styles.mobileSubText}>
            {moment(review.createdAt).locale('ru').format('DD.MM.YYYY HH:mm')}
            {review.edited_at && ' · изменён'}
          </Typography.Text>
          {!!getMediaLabel(review) && (
            <Typography.Text type='secondary' className={styles.mobileSubText}>
              {getMediaLabel(review)}
            </Typography.Text>
          )}
        </div>
      )
    },
    {
      title: 'Оценка и статус',
      dataIndex: 'rating',
      width: '40%',
      render: (_, { rating, status }) => (
        <div className={styles.mobileCell}>
          <Typography.Text>{rating} из 5</Typography.Text>
          <Tag color={getReviewStatusColor(status)}>
            {getReviewStatusLabel(status)}
          </Tag>
        </div>
      )
    }
  ]

  const entityLink = selectedReview
    ? getEntityLink(selectedReview, isSellerView)
    : null

  // Покупателя и магазин открывает только админ площадки
  const authorLink =
    !isSellerView && selectedReview?.author?._id
      ? superAdminRoutes.USERS_EDIT.replace(':id', selectedReview.author._id)
      : null

  const shopLink = selectedReview?.shop?._id
    ? superAdminRoutes.SELLER.replace(':id', selectedReview.shop._id)
    : null

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <MainTitle text='Отзывы' style={{ marginBottom: 0 }} />
        {!isSellerView && !sellerId && (
          <Checkbox
            checked={showPublished}
            onChange={(e) => setShowPublished(e.target.checked)}
          >
            Показать опубликованные
          </Checkbox>
        )}
      </div>
      <Table
        columns={isMobile ? mobileColumns : columns}
        size={isMobile ? 'small' : undefined}
        tableLayout={isMobile ? 'fixed' : undefined}
        dataSource={reviews}
        loading={isLoading}
        rowKey={(review) => review._id}
        onRow={(review) => ({
          onClick: () => setSelectedReview(review)
        })}
        pagination={{
          current: currentPage,
          onChange: (page) => {
            setCurrentPage(page)
            getReviews((page - 1) * PAGE_SIZE)
          },
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false
        }}
      />
      <Modal
        centered
        open={Boolean(selectedReview)}
        footer={null}
        onCancel={() => setSelectedReview(null)}
        width={720}
      >
        {selectedReview && (
          <>
            <Typography.Title level={4}>
              Отзыв на «{getEntityName(selectedReview)}»
            </Typography.Title>
            <div className={styles.detailsHead}>
              <div className={styles.detailsItem}>
                <Typography.Text className={styles.detailsLabel}>
                  Автор отзыва
                </Typography.Text>
                {renderDetailsValue(
                  getAuthorName(selectedReview, isSellerView),
                  authorLink
                )}
                {!isSellerView && selectedReview.anonymity && (
                  <Typography.Text
                    type='secondary'
                    className={styles.detailsNote}
                  >
                    На сайте отзыв показывается анонимно
                  </Typography.Text>
                )}
              </div>
              {!isSellerView && (
                <div className={styles.detailsItem}>
                  <Typography.Text className={styles.detailsLabel}>
                    Магазин
                  </Typography.Text>
                  {renderDetailsValue(
                    selectedReview.shop?.shop_name ?? '—',
                    shopLink
                  )}
                </div>
              )}
              <div className={styles.detailsItem}>
                <Typography.Text className={styles.detailsLabel}>
                  {getReviewEntityTypeLabel(selectedReview.entity_type)}
                </Typography.Text>
                {renderDetailsValue(getEntityName(selectedReview), entityLink)}
              </div>
              <div className={styles.detailsItem}>
                <Typography.Text className={styles.detailsLabel}>
                  Номер заказа
                </Typography.Text>
                <Typography.Text>
                  {selectedReview.order?.number ?? '—'}
                </Typography.Text>
              </div>
              <div className={styles.detailsItem}>
                <Typography.Text className={styles.detailsLabel}>
                  Дата отзыва
                </Typography.Text>
                <Typography.Text>
                  {moment(selectedReview.createdAt).locale('ru').format('LLL')}
                  {selectedReview.edited_at && ` · изменён ${moment(selectedReview.edited_at).locale('ru').format('LLL')}`}
                </Typography.Text>
              </div>
              <div className={styles.detailsItem}>
                <Typography.Text className={styles.detailsLabel}>
                  Статус
                </Typography.Text>
                <Tag color={getReviewStatusColor(selectedReview.status)}>
                  {getReviewStatusLabel(selectedReview.status)}
                </Tag>
              </div>
            </div>
            <div className={styles.reviewBody}>
              <Rate disabled value={selectedReview.rating} />
              <Typography.Paragraph className={styles.reviewText}>
                {selectedReview.text || 'Текст отзыва отсутствует'}
              </Typography.Paragraph>
              {!!getMediaLabel(selectedReview) && (
                <div className={styles.reviewMedia}>
                  {selectedReview.videos?.map((video) => (
                    <video
                      key={video.src}
                      src={`${IMAGES_URL}${video.src}`}
                      poster={video.poster ? `${IMAGES_URL}${video.poster}` : undefined}
                      controls
                      preload='metadata'
                      aria-label='Видео из отзыва'
                      className={styles.reviewVideo}
                    />
                  ))}
                  {!!selectedReview.images?.length && (
                    <div className={styles.reviewImages}>
                      <Image.PreviewGroup>
                        {selectedReview.images.map((key) => (
                          <Image
                            key={key}
                            src={`${IMAGES_URL}${key}`}
                            alt='Фото из отзыва'
                            rootClassName={styles.reviewImage}
                            preview={{ mask: null }}
                          />
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.modalActions}>
              <Space>
                <Button onClick={() => setSelectedReview(null)}>Закрыть</Button>
                {!isSellerView && (
                  <>
                    <Button
                      danger
                      loading={isStatusLoading}
                      disabled={
                        selectedReview.status === EReviewStatus.REJECTED
                      }
                      onClick={() => updateStatus(EReviewStatus.REJECTED)}
                    >
                      Отклонить
                    </Button>
                    <Button
                      type='primary'
                      loading={isStatusLoading}
                      disabled={
                        selectedReview.status === EReviewStatus.PUBLISHED
                      }
                      onClick={() => updateStatus(EReviewStatus.PUBLISHED)}
                    >
                      Опубликовать
                    </Button>
                  </>
                )}
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
